<template>
  <section class="cloning-overlay">
    <header class="flex items-start justify-between">
      <div>
        <h3>{{search.name}}</h3>
        <p>{{$t('booleanSearch.tags.link_tag_to_search')}}</p>
      </div>
      <div class="cursor-pointer teal-hover" @click="triggerClose">
        <i class="far fa-times-circle"/>
      </div>
    </header>

    <section class="form-section" :class="{'display-none': !linkTagsError, 'p-0': true}">
      <ul ref="error_msgs" class="error-msgs">
        <li v-if="linkTagsError && linkTagsError !== ''">{{linkTagsError}}</li>
      </ul>
    </section>
    
    <form-renderer
      v-if="!openCreateForm"
      :key="pageKey"
      id="search-tag-add-form"
      form-wrapper-class="add-tag-wrapper"
      :model="model"
      :fields="['boolSearchTags']"
      :fieldsAdditions="addFieldsAdditions"
      submitButtonClass="button-save mt10"
      @onSave="updateLinkedTags"
      @updateModel="(value) => updateModel({ tags: normalizeTags(value.tags) }, 'tags')"
    />
    <form-renderer
      v-else
      :key="formPageKey"
      id="search-tag-create-form"
      form-wrapper-class="add-tag-wrapper"
      :model="model"
      :fields="createFormFields"
      :fieldsAdditions="tagDetailsFieldsAdditions"
      submitButtonClass="button-save mt10"
      submitButtonLabel="booleanSearch.tags.create"
      @onSave="submitCreateTag"
      @updateModel="(value, field) => updateModel(value, field.name)"
    />
  </section>

</template>

<script>
// Plugins
import { mapState, mapActions } from 'vuex';
// Components
import FormRenderer from '@/components/form-renderer';
// Mixins
import formsStoreData from '@/mixins/boolean-search/formsStoreData';

export default {
  name: "CreateTagOverlay",

  props: {
    search: {
      type: Object,
      required: true
    },
    searchQueryKeys: {
      type: Array,
      default: () => []
    },
    close: {
      type: Function
    },
    updateSearchTags: {
      type: Function
    }
  },

  mixins: [ formsStoreData ],

  data () {
    return {
      model: { 
        tags: this.normalizeTags(this.search.tags), 
        name: '', 
        category: null, 
        is_private: false 
      },
      openCreateForm: false,
      pageKey: 0,
      formPageKey: 0
    }
  },

  computed: {
    ...mapState({
      project_id: state => state.booleanSearch.project_id,
      showPrivateTags: state => state.booleanSearch.tag_library.showPrivateTags,
      compatibleTags: state => state.booleanSearch.searches.compatible_tags,
      linkTagsError: state => state.booleanSearch.searches.link_tags_error
    }),

    createFormFields () {
      return ['name', 'boolTagCategory'].concat(this.showPrivateTags ? ['isPrivateTag'] : []) 
    },

    addFieldsAdditions() {
      return {
        boolSearchTags: {
          options: this.compatibleTagOptions,
          taggable: this.hasQuery,
          onTag: (value) => this.toggleOpenCreateForm(true, value),
          onSearchChange: (value) => this.onSearchChange(value)
        }
      };
    },

    compatibleTagOptions () {
      const tagsById = new Map();
      [
        ...this.normalizeTags(this.model.tags), 
        ...this.compatibleTags
      ].forEach(tag => {
        if (!tag) return;
        const key = tag.id || `new:${tag.name}`;
        tagsById.set(key, tag);
      });

      return [...tagsById.values()].map(t => ({
        value: t,
        label: t.name
      }));
    },

    hasQuery() {
      return this.searchQueryKeys?.filter(key => this.search[key]?.length > 0).length > 0 || 
        (['x_netfeedr', 'x_netfeedr_channel', 'twitter_search_v3'].indexOf(this.search.search_type) >= 0 && this.search.standard_query?.length > 0) ||
        (this.search.is_multi >= 0 && this.search.standard_query?.length > 0);
    },
  },

  methods: {
    ...mapActions({
      getSearchCompatibleTags: 'booleanSearch/getSearchCompatibleTags',
      batchSaveSearchLinkedTags: 'booleanSearch/batchSaveSearchLinkedTags'
    }),

    updateLinkedTags() {
      if (!this.haveTagsChanged()) {
        this.triggerClose();
        return;
      }

      const tagUpdates = this.generateTagOperations();

      this.batchSaveSearchLinkedTags({ projectId: this.project_id, searchId: this.search.id, tagUpdates }).then((data) => {
        if (!data.valid) return;

        this.notifyStoreSuccess(this.$t('booleanSearch.tags.tags_updated_successfully'));
        this.$emit('updateSearchTags', data.tags);
        this.triggerClose();
      });
    },

    normalizeTags(tags) {
      if (!tags) return [];
      return Array.isArray(tags) ? tags : [tags];
    },

    haveTagsChanged() {
      const oldTags = this.normalizeTags(this.search.tags);
      const newTags = this.normalizeTags(this.model.tags);
      const oldIds = oldTags.map(tag => tag.id).filter(Boolean).sort().join(',');
      const newIds = newTags.map(tag => tag.id).filter(Boolean).sort().join(',');

      if (oldIds !== newIds) return true;

      const oldPending = oldTags.filter(tag => !tag.id).map(tag => `${tag.name}:${tag.category}`).sort().join(',');
      const newPending = newTags.filter(tag => !tag.id).map(tag => `${tag.name}:${tag.category}`).sort().join(',');

      return oldPending !== newPending;
    },

    generateTagOperations() {
      const oldTags = this.normalizeTags(this.search.tags);
      const newTags = this.normalizeTags(this.model.tags);
      const oldIds = oldTags.map(tag => tag.id);
      const newIds = newTags.filter(tag => tag.id).map(tag => tag.id);
      
      return {
        tags_to_link: newTags.filter(tag => tag.id && !oldIds.includes(tag.id)).map(tag => tag.id),
        tags_to_unlink: oldTags.filter(tag => !newIds.includes(tag.id)).map(tag => tag.id),
        tag_to_create: newTags.filter(tag => !tag.id && tag.name).map(tag => ({
          name: tag.name,
          category: tag.category,
          is_private: tag.is_private
        }))
      };
    },

    submitCreateTag(val) {
      const newTags = [
        ...this.normalizeTags(this.model.tags),
        { name: val.name, category: val.category, is_private: val.is_private }
      ];
      this.updateModel({ tags: newTags }, 'tags');
      this.toggleOpenCreateForm(false);
    },

    updateModel(newValue, field) {
      if(!field) return;
      this.model[field] = newValue[field];
    },

    toggleOpenCreateForm(isOpen, value) { 
      if(isOpen === undefined) isOpen = !this.openCreateForm;

      this.pageKey++;
      this.formPageKey++;

      this.openCreateForm = isOpen;
      if(isOpen && value.label) this.updateModel({ name: value.label }, 'name');


      if(!isOpen) this.resetModel();
      this.resetSaveError();
    },

    triggerClose() {
      this.resetSaveError();

      if(this.openCreateForm) {
        this.toggleOpenCreateForm();
      }
      else {
        this.$emit('close');
      }
    },

    resetModel() {
      this.model = {
        tags: this.normalizeTags(this.model.tags).filter(tag => typeof tag !== 'string'),
        name: '', 
        category: null, 
        is_private: false
      };
    },

    resetSaveError() {
      if(this.linkTagsError !== '') {
        this.$store.commit('booleanSearch/SET_LINKED_TAGS_ERROR', "");
      }
    },

    onSearchChange(value) {
      if (value != "") {
        this.getSearchCompatibleTags({ projectId: this.project_id, query: value })
      }
    }
  },

  components: {
    FormRenderer
  }
}
</script>